import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../../App"
import { Block, BlockContainer, BlockRow, BlockField } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { OverlayScrollBody } from "../../Components/OverlayScrollBody"
import { AccountApi, ApiClient, IAppState, ILogbookSyncStatus, IShipImportStatus, Ship, ShipApi } from "../../Types"
import { AboutView } from "../AboutView"
import { ChangeLogView } from "../ChangeLogView"
import { AppRoutes } from "./Router"
import { CloseShipConfirmView, CloseShipForceWarningView } from "./CloseShipViews"
import { GetAppView } from "./Context"
import { LoginViewData } from "../Login/Context"
import { OverlayView } from "../OverlayView"
import { SessionExpiredView } from "./SessionExpiredView"
import { ShipSyncStatusMessageView } from "../ShipSyncStatusView"
import { UserSettingsView } from "../UserSettingsView"
import { CompanionCloseGuard, CompanionConnectView } from "../ShipSelect/ConnectView"
import { localStorageId } from "../../App/LocalStorage"

export const AppOverlayContentViewData = {
  deleteImport(api: ApiClient, shipId: number) {
    return new ShipApi(api).ships.deleteImport({ params: { shipId } }).call();
  },
  deleteAccount(api: ApiClient) {
    return new AccountApi(api).account.delete({ params: {} }).call();
  },
  syncFailed(status?: ILogbookSyncStatus): boolean {
    return status === ILogbookSyncStatus.failed ||
      status === ILogbookSyncStatus.disconnected ||
      status === ILogbookSyncStatus.initialSyncFailed ||
      // A dead cloud session is a sync failure like any other as far as every caller of
      // this helper is concerned -- nothing reaches the cloud until it is fixed (#1449).
      status === ILogbookSyncStatus.sessionExpired;
  },
  // Re-login from the expired-session prompt. Deliberately the same call the login
  // route makes, so a session established here is indistinguishable from a normal one;
  // the app then reclaims this device's ship by its `clientToken` and restarts sync.
  //
  // Returns whether it worked WITHOUT touching anything on a failure: `setSession` runs
  // only for an authorized response, so a refused attempt leaves the existing session,
  // ship and claim exactly as they were (#1449).
  async sessionLogin(app: IAppState, email: string, password: string): Promise<boolean> {
    const response = await LoginViewData.login(app.apiClient, { email, password, client: app.cloudSession() ? "cloud" : undefined });
    if (response && response.session && response.session.authorized && response.session.id) {
      app.setSession(response.session, response.type, response.account, response.user, response.features, response.accountUserLevel);
      return true;
    }
    return false;
  },
};

export enum AppOverlayContent {
  none = "none",
  about = "about",
  changeLog = "changeLog",
  closeShip = "closeShip",
  connectLogbook = "connectLogbook",
  importShip = "importShip",
  logout = "logout",
  removeAccount = "removeAccount",
  selectShip = "selectShip",
  selectShipFailed = "selectShipFailed",
  sessionExpired = "sessionExpired",
  shipListOptions = "shipListOptions",
  shipNotClosed = "shipNotClosed",
  shipOpen = "shipOpen",
  userSettings = "userSettings",
}

export interface AppOverlayContentOptions {
  shipId?: number;
  selectShipError?: string;
  // Whether the companion-connect dialog should try the automatic (Bonjour) route
  // before offering manual address entry. Captured at click time so volatile discovery
  // cannot change the dialog's shape mid-attempt (#1279).
  nearby?: boolean;
}

interface AppOverlayContentViewProps {
}

export function AppOverlayContentView(_: AppOverlayContentViewProps) {
  const app = GetApp();
  const t = app.getT("AppOverlayContentView");
  const { appOverlay, appOverlayOptions, connected, deselectShip, forceCloseShip, navigateApp, resetShip, resetAppOverlay, setAppOverlay, ships, shipClosing, shipCloseFailed, shipCloseFailedStatus } = GetAppView();

  const overlayShip = useMemo(() => {
    return ships?.find((s) => s.id === appOverlayOptions.shipId);
  }, [appOverlayOptions, ships]);

  const [importResetState, setImportResetState] = useState<"idle" | "confirm" | "resetting">("idle");
  const [forceCloseConfirm, setForceCloseConfirm] = useState(false);
  const companionCloseGuard = useRef<CompanionCloseGuard | undefined>(undefined);
  const overlayGeneration = useRef(0);
  const closingGeneration = useRef<number | undefined>(undefined);

  const registerCompanionCloseGuard = useCallback((guard: CompanionCloseGuard | undefined) => {
    companionCloseGuard.current = guard;
  }, []);

  useEffect(() => {
    return () => { overlayGeneration.current += 1; };
  }, [appOverlay, appOverlayOptions.shipId]);

  const withCompanionCleanup = useCallback(async (action: () => void) => {
    const guard = appOverlay === AppOverlayContent.shipOpen ? companionCloseGuard.current : undefined;
    if (!guard) {
      action();
      return;
    }
    const generation = overlayGeneration.current;
    if (closingGeneration.current === generation) return;
    closingGeneration.current = generation;
    try {
      if (await guard() && overlayGeneration.current === generation) action();
    } finally {
      if (closingGeneration.current === generation) closingGeneration.current = undefined;
    }
  }, [appOverlay]);

  useEffect(() => {
    setImportResetState("idle");
    setForceCloseConfirm(false);
  }, [appOverlay]);

  const resetImport = useCallback(async () => {
    if (!overlayShip) {
      return;
    }
    setImportResetState("resetting");
    await AppOverlayContentViewData.deleteImport(app.apiClient, overlayShip.id);
    resetAppOverlay();
  }, [overlayShip, resetAppOverlay]);

  return (
    <OverlayView className="AppOverlay" overlay={appOverlay} overlays={{
      [AppOverlayContent.none]: {
        render: () => null,
      },

      [AppOverlayContent.about]: {
        close: true,
        render: () => <AboutView />,
      },

      [AppOverlayContent.changeLog]: {
        close: true,
        render: () => <ChangeLogView showExternal={true} onCloseExternal={resetAppOverlay} />,
      },

      [AppOverlayContent.closeShip]: {
        close: true, title: t("ship_close_confirm_title"), style: "warning", className: "overlay-max-height",
        render: () => {
          const ship = overlayShip || ships?.find((s) => s.id === app.shipId);
          const noInternet = !navigator.onLine;

          if (forceCloseConfirm) {
            return (
              <CloseShipForceWarningView
                shipName={ship?.name}
                shipClosing={shipClosing}
                onCancel={() => setForceCloseConfirm(false)}
                onForceCloseShip={forceCloseShip}
                t={t}
              />
            );
          }

          return (
            <CloseShipConfirmView
              shipName={ship?.name}
              noInternet={noInternet}
              syncFailed={AppOverlayContentViewData.syncFailed(app.status?.sync?.status)}
              shipClosing={shipClosing}
              shipCloseFailed={shipCloseFailed}
              shipCloseFailedStatus={shipCloseFailedStatus}
              onCancel={resetAppOverlay}
              onCloseShip={resetShip}
              onForceCloseShip={() => setForceCloseConfirm(true)}
              t={t}
            />
          );
        },
      },

      [AppOverlayContent.importShip]: {
        close: true, title: t("import_ship_title"), className: "overlay-max-height",
        render: () => {
          return (
            <OverlayScrollBody className="Overlay-body Overlay-body-padding message-text-bigger" readMoreLabel={t("read_more")}>
              <Block>
                <BlockRow>
                  <LocalizedMessage message={(m) => m.importShip.text} />
                </BlockRow>
              </Block>
            </OverlayScrollBody>
          );
        },
      },

      [AppOverlayContent.logout]: {
        close: true, title: t("logout_confirm_title"), style: "warning", className: "overlay-max-height",
        render: () => {
          const ship = ships?.find((s) => s.id === app.shipId);
          const noInternet = !navigator.onLine;
          const isPrimaryShipOpen = app.primarySession() && app.shipId;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
          let message = (m: any) => m.logout.logoutWithInternet;
          if (noInternet) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
            message = (m: any) => m.logout.logoutWithoutInternet;
          }

          return (
            <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")}>
              <Block>
                <BlockRow>
                  <LocalizedMessage html={true} message={message} replace={{ name: ship?.name || "" }} />
                </BlockRow>
                {isPrimaryShipOpen && (
                  <>
                    <hr />
                    <BlockRow>
                      <LocalizedMessage html={true} message={(m) => m.shipClose.keepOpen} />
                    </BlockRow>
                  </>
                )}
                <BlockRow actions={true} className="block-row-one-row">
                  <BlockField>
                    <button className="button" onClick={resetAppOverlay}>{t("logout_confirm_cancel")}</button>
                  </BlockField>
                  {!noInternet && (
                    <BlockField>
                      <button className="button default" onClick={async () => {
                        resetAppOverlay();
                        await app.logout();
                      }}
                      >{t("logout_confirm")}</button>
                    </BlockField>
                  )}
                </BlockRow>
              </Block>
            </OverlayScrollBody>
          );
        },
      },

      [AppOverlayContent.removeAccount]: {
        close: true,
        title: t("remove_account_confirm_title"),
        render: () => {
          return (
            <div className="Overlay-body">
              <Block>
                <BlockRow>
                  <LocalizedMessage message={(m) => m.subscription.removeAccount} replace={{ account: app.account?.name || "Untitled", user: app.user?.name || "Untitled" }} />
                </BlockRow>
                <BlockRow actions={true}>
                  <BlockField>
                    <button className="button" onClick={resetAppOverlay}>{t("remove_account_cancel")}</button>
                  </BlockField>
                  <BlockField>
                    <button className="button default" onClick={async () => {
                      const response = await AppOverlayContentViewData.deleteAccount(app.apiClient);
                      if (response && response.success) {
                        resetAppOverlay();
                        await app.logout(false);
                        window.location.reload();
                      }
                    }}
                    >{t("remove_account_confirm")}</button>
                  </BlockField>
                </BlockRow>
              </Block>
            </div>
          );
        },
      },

      [AppOverlayContent.selectShip]: {
        render: () => <ShipSyncStatusMessageView status={ILogbookSyncStatus.selectShip} />,
      },

      [AppOverlayContent.selectShipFailed]: {
        render: () => <ShipSyncStatusMessageView status={ILogbookSyncStatus.selectShipFailed} error={appOverlayOptions.selectShipError} />,
      },

      [AppOverlayContent.sessionExpired]: {
        // `close: true` on purpose -- the prompt is dismissable and destroys nothing, so
        // the user can carry on logging their trip locally while the cloud is out of
        // reach (#1449). "alarm" for the same red treatment the connection error gets.
        close: true, title: t("session_expired_title"), style: "alarm", className: "overlay-max-height",
        render: () => (
          <SessionExpiredView
            onDismiss={resetAppOverlay}
            onLogin={(email, password) => AppOverlayContentViewData.sessionLogin(app, email, password)}
            t={t}
          />
        ),
      },

      [AppOverlayContent.shipListOptions]: {
        close: true,
        title: t("options_ship", { name: overlayShip?.name || "" }),
        className: "overlay-max-height",
        render: () => {
          const shipData = overlayShip ? Ship.parse(overlayShip).dataParsed : undefined;
          return (
            <OverlayScrollBody className="Overlay-body Overlay-body-padding message-text-bigger" readMoreLabel={t("read_more")} deps={[importResetState]}>
              <Block>
                <BlockRow>
                  <h3 className="block-row-0">{t("options_ship_title")}</h3>
                </BlockRow>
                <BlockRow>
                  {t("options_update")} {overlayShip?.syncedAt ? new Date(overlayShip.syncedAt).toLocaleString() : "-"}
                </BlockRow>
                {/* TODO: Implement ship statistics API endpoint to fetch:
                - First day (min day number across all trips/cruises)
                - Last day (max day number across all trips/cruises)
                - Total count of days
                - Total count of trips
                - Total count of cruises
                Then fetch and display the actual data here
            <BlockRow>{t("options_first_day")} -</BlockRow>
            <BlockRow>{t("options_last_day")} -</BlockRow>
            <BlockRow>{t("options_days")} -</BlockRow>
            <BlockRow>{t("options_trips")} -</BlockRow>
            <BlockRow>{t("options_cruises")} -</BlockRow>
            <br></br> */}

                {app.cloudSession() && overlayShip && overlayShip.clientToken && (
                  <>
                    <hr></hr>
                    <BlockRow>
                      <h3 className="block-row-0">{t("disconnect_ship_title")}</h3>
                    </BlockRow>

                    <BlockRow>
                      <LocalizedMessage message={(m) => connected ? m.shipInfo.disconnect : m.connection.interrupted} replace={{ name: overlayShip?.name || "" }} />
                    </BlockRow>

                    <BlockRow actions={true} className="block-row-one-row">
                      <BlockField>
                        <button className="button ship-deselect-button default" onClick={() => deselectShip(overlayShip.id)} disabled={!overlayShip}>
                          {t("disconnect_ship")}
                        </button>
                      </BlockField>
                    </BlockRow>
                  </>
                )}
                {app.cloudSession() && overlayShip && overlayShip.clientToken == undefined && (
                  <>
                    {(shipData && shipData.importStatus && shipData.importStatus != IShipImportStatus.failed) ? (
                      <>
                        {shipData.importStatus == IShipImportStatus.complete ? (
                          <>
                            {importResetState === "confirm" ? (
                              <>
                                <BlockRow>
                                  <h4 className="text-centered text-warning">{t("import_reset_confirm_title")}</h4>
                                </BlockRow>
                                <BlockRow className="message-text-bigger">
                                  {t("import_reset_confirm_text")}
                                </BlockRow>
                                <BlockRow actions={true} className="block-row-one-row">
                                  <BlockField>
                                    <button className="button" onClick={() => setImportResetState("idle")}>{t("import_reset_confirm_no")}</button>
                                  </BlockField>
                                  <BlockField>
                                    <button className="button default" onClick={resetImport}>{t("import_reset_confirm_yes")}</button>
                                  </BlockField>
                                </BlockRow>
                              </>
                            ) : importResetState === "resetting" ? (
                              <>
                                <BlockRow>
                                  <h4 className="text-centered text-warning">{t("import_resetting")}</h4>
                                </BlockRow>
                              </>
                            ) : (
                              <>
                                <BlockRow>
                                  <h4 className="text-centered text-success">{t("import_status_complete", { date: app.format("date", shipData.importCompletedAt) })}</h4>
                                </BlockRow>
                                <BlockRow className="row-right block-row-one-row">
                                  <button className="button default" onClick={() => setImportResetState("confirm")}>{t("import_reset")}</button>
                                </BlockRow>
                              </>
                            )}
                          </>
                        ) : (
                          <BlockRow>
                            <h4 className="text-centered text-warning">{t("import_status_in_progress", { time: app.format("timeAgoDate", shipData.importStartedAt) })}</h4>
                          </BlockRow>
                        )}
                      </>
                    ) : (
                      <>
                        <hr />
                        <BlockRow>
                          <h3 className="block-row-0">{t("import_trips")}</h3>
                        </BlockRow>
                        <BlockRow className="message-text-bigger ">
                          <LocalizedMessage message={(m) => m.importTrips.text} replace={{ name: overlayShip.name }} />
                        </BlockRow>
                        {shipData?.importStatus == IShipImportStatus.failed && (
                          <BlockRow className="text-error">
                            <h4 className="text-centered">{t("import_status_failed")}</h4>
                          </BlockRow>
                        )}
                        <BlockRow className="row-right block-row-one-row">
                          <button className="button default" onClick={() => { navigateApp(AppRoutes.ship, { shipId: overlayShip.id, shipRoute: "import" }); resetAppOverlay(); }}>{t("import_trips")}</button>
                        </BlockRow>
                      </>
                    )}
                  </>
                )}
              </Block>
            </OverlayScrollBody>
          );
        },
      },

      [AppOverlayContent.shipOpen]: {
        close: true,
        title: t("ship_open_title", { name: overlayShip?.name || "" }), style: "warning", className: "overlay-max-height",
        render: () => {
          // One surface for both companion-connect routes: a spinner while the
          // automatic (Bonjour) attempt runs, falling back to this same dialog's
          // manual address form -- with the reason it fell back -- on failure or
          // timeout. Previously a LAN-discovered ship never reached this dialog at
          // all and its connect failed silently (#1279).
          return (
            <CompanionConnectView
              shipId={overlayShip?.id || 0}
              shipName={overlayShip?.name || ""}
              nearby={appOverlayOptions.nearby === true}
              onCancel={resetAppOverlay}
              onCloseGuard={registerCompanionCloseGuard}
              footer={(cancel) => {
                const shipId = overlayShip?.id;
                const pendingCompanion = shipId !== undefined &&
                  !app.cloudSession() && !app.localSession() &&
                  app.session?.shipId !== shipId &&
                  localStorageId("logbook-companion") === shipId;
                return (
                  <Block>
                    <BlockRow actions={true} className="block-row-one-row">
                      <BlockField>
                        <button className="button" onClick={cancel}>{t("cancel")}</button>
                      </BlockField>
                      <BlockField>
                        <button className="button default" onClick={() => {
                          if (pendingCompanion) {
                            void cancel();
                          } else {
                            void withCompanionCleanup(() => setAppOverlay(AppOverlayContent.shipNotClosed, { shipId }));
                          }
                        }}
                        >{t("disconnect_logbook")}</button>
                      </BlockField>
                    </BlockRow>
                  </Block>
                );
              }}
            />
          );
        },
      },

      [AppOverlayContent.shipNotClosed]: {
        close: true,
        title: t("ship_not_closed_title", { name: overlayShip?.name || "" }), style: "alarm", className: "overlay-max-height",
        render: () => {
          return (
            <>
              <div className="Overlay-body message-text-bigger">
                <Block>
                  <BlockRow className="message-text-bigger">
                    <LocalizedMessage message={(m) => m.noteText.shipNotClosed} replace={{ name: overlayShip?.name || "" }} />
                  </BlockRow>
                </Block>
              </div>
              <div className="Overlay-footer">
                <Block>
                  <BlockRow actions={true}>
                    <BlockField>
                      <button className="button" onClick={resetAppOverlay}>{t("cancel")}</button>
                    </BlockField>
                    <BlockField>
                      <button className="button defaults" onClick={async () => {
                        if (await app.openServer("account")) {
                          resetAppOverlay();
                        }
                      }}
                      >{t("ship_view_ships_cloud")}</button>
                    </BlockField>
                  </BlockRow>
                </Block>
              </div>
            </>
          );
        },
      },

      [AppOverlayContent.userSettings]: {
        close: true,
        title: t("user_settings"),
        className: "overlay-narrow",
        render: () => {
          return (
            <BlockContainer className="Overlay-body">
              <UserSettingsView />
            </BlockContainer>
          );
        },
      },

    }} resetOverlay={() => { void withCompanionCleanup(resetAppOverlay); }}
    />
  );
}
